import { CollectionDto, CollectionLook } from "../collection";
import { ProductDto } from "../product";
import { QueryClient } from "@tanstack/react-query";
import { normalizeLook, NormalizedLook, denormalizeLook } from "./look";
import { createMemoCache } from "./utils";

/**
 * Normalized version of CollectionDto
 *
 * Replaces the products array with productIds array and looks array with lookIds
 * to:
 * 1. Prevent duplication of product/look data across multiple collections
 * 2. Allow independent product/look cache updates
 * 3. Support efficient data fetching patterns
 */
export type NormalizedCollection = Omit<CollectionDto, "products" | "looks"> & {
  productIds: string[];
  lookIds: string[];
};

/**
 * Extended error type for denormalization errors
 */
export class CollectionDenormalizationError extends Error {
  constructor(
    message: string,
    public readonly missingIds: { products: string[]; looks: string[] } = { products: [], looks: [] }
  ) {
    super(message);
    this.name = "CollectionDenormalizationError";
  }
}

// LRU cache for memoization of denormalized collections
const memoCache = createMemoCache<string, CollectionDto>(100, 60 * 1000);

/**
 * Normalizes a collection by extracting products and looks and storing them in their respective caches
 * Returns a modified collection object where the products and looks arrays are replaced with productIds and lookIds
 *
 * Note: With the optimized collection DTO structure:
 * - `collection.productIds` contains the COMPLETE list of all product IDs
 * - `collection.products` may contain only PREVIEW products (first N items)
 * - We cache whatever products are provided, but use productIds from the DTO
 *
 * @param queryClient - QueryClient instance
 * @param collection - Original collection DTO from the API
 * @returns NormalizedCollection - Collection with products and looks replaced by their IDs
 */
export function normalizeCollection(queryClient: QueryClient, collection: CollectionDto): NormalizedCollection {
  // Cache preview products from the collection
  const products = collection.products || [];
  products.forEach((product: ProductDto) => {
    queryClient.setQueryData(["product", product.id], product);
  });

  // Get looks from the collection and normalize them
  const collectionLooks = collection.looks || [];
  collectionLooks.forEach((collectionLook: CollectionLook) => {
    const look = collectionLook.look;

    // Normalize the look and store in cache (which also stores its products)
    if (look) {
      const normalizedLook = normalizeLook(queryClient, look);
      queryClient.setQueryData(["look", look.id], normalizedLook);
    }
  });

  // Create a normalized version of the collection
  // Use productIds and lookIds from the DTO (complete lists) rather than deriving from products array
  const { products: _, looks: __, ...restCollection } = collection;
  const normalizedCollection: NormalizedCollection = {
    ...restCollection,
    // Use the complete productIds/lookIds lists from the DTO
    // These are provided by the backend and include ALL IDs, not just preview items
    // Use ?? (nullish coalescing) instead of || to handle empty arrays correctly
    productIds: collection.productIds ?? products.map((product) => product.id),
    lookIds: collection.lookIds ?? collectionLooks.map((cl) => cl.look?.id).filter(Boolean) as string[],
  };

  return normalizedCollection;
}

/**
 * Batch normalizes multiple collections at once
 * More efficient than calling normalizeCollection multiple times for large datasets
 *
 * Note: With the optimized collection DTO structure:
 * - `collection.productIds` contains the COMPLETE list of all product IDs
 * - `collection.products` may contain only PREVIEW products (first N items)
 * - We cache whatever products are provided, but use productIds from the DTO
 *
 * @param queryClient - QueryClient instance
 * @param collections - Array of collections to normalize
 * @returns Array of normalized collections
 */
export function batchNormalizeCollections(
  queryClient: QueryClient,
  collections: CollectionDto[]
): NormalizedCollection[] {
  // Track which IDs we've already cached to avoid duplicates
  const seenProducts = new Set<string>();
  const seenLooks = new Set<string>();

  // Single pass: dedupe + cache preview products and looks
  collections.forEach((collection) => {
    // Cache preview products
    (collection.products || []).forEach((product) => {
      if (!seenProducts.has(product.id)) {
        seenProducts.add(product.id);
        queryClient.setQueryData(["product", product.id], product);
      }
    });

    // Cache looks and their products
    (collection.looks || []).forEach(({ look }) => {
      if (look && !seenLooks.has(look.id)) {
        seenLooks.add(look.id);

        // normalizeLook also caches its products
        const normLook = normalizeLook(queryClient, look);
        queryClient.setQueryData(["look", look.id], normLook);
      }
    });
  });

  // Build normalized collection DTOs using productIds/lookIds from the DTO (complete lists)
  return collections.map((collection) => {
    const { products: _, looks: __, ...rest } = collection;
    
    // Use the complete productIds/lookIds from the DTO, falling back to deriving from arrays
    // for backwards compatibility with older API responses
    // Use ?? (nullish coalescing) instead of || to handle empty arrays correctly
    const productIds = collection.productIds ?? (collection.products ?? []).map((p) => p.id);
    const lookIds = collection.lookIds ?? (collection.looks ?? []).map(({ look }) => look?.id).filter(Boolean) as string[];
    
    return {
      ...rest,
      productIds,
      lookIds,
    };
  });
}

/**
 * Denormalizes a collection by fetching products and looks from their respective caches
 * and reconstructing the original CollectionDto structure with proper error handling
 *
 * @param queryClient - QueryClient instance
 * @param normalizedCollection - Normalized collection from the cache
 * @param options - Configuration options
 * @returns CollectionDto - Collection with full product and look objects
 * @throws DenormalizationError if products/looks are missing and throwOnMissing is true
 */
export function denormalizeCollection(
  queryClient: QueryClient,
  normalizedCollection: NormalizedCollection,
  options: {
    throwOnMissing?: boolean;
    includePartial?: boolean;
  } = {}
): CollectionDto {
  // Default options
  const { throwOnMissing = false, includePartial = true } = options;

  // Generate cache key for memoization
  const cacheKey = `${normalizedCollection.id}:${normalizedCollection.productIds.join(
    ","
  )}:${normalizedCollection.lookIds.join(",")}`;

  // Check if we have a cached result that's still valid
  const cachedResult = memoCache.get(cacheKey);
  if (cachedResult) {
    return cachedResult;
  }

  // Get product IDs from the normalized collection
  const productIds = normalizedCollection.productIds || [];
  const lookIds = normalizedCollection.lookIds || [];
  const missingIds = { products: [] as string[], looks: [] as string[] };

  // Get products from the product cache
  const products = productIds
    .map((id) => {
      const product = queryClient.getQueryData<ProductDto>(["product", id]);
      if (!product) missingIds.products.push(id);
      return product;
    })
    .filter(Boolean) as ProductDto[];

  // Get looks from the look cache
  const looks = lookIds
    .map((id) => {
      const look = queryClient.getQueryData<NormalizedLook>(["look", id]);
      if (!look) {
        missingIds.looks.push(id);
        return null;
      }

      // Denormalize the look (which also gets its products)
      try {
        return {
          look: denormalizeLook(queryClient, look, options),
        } as CollectionLook;
      } catch (error) {
        if (error instanceof Error) {
          missingIds.looks.push(id);
        }
        return null;
      }
    })
    .filter(Boolean) as CollectionLook[];

  // Handle missing items based on options
  if ((missingIds.products.length > 0 || missingIds.looks.length > 0) && throwOnMissing) {
    throw new CollectionDenormalizationError(
      `Failed to denormalize collection: ${missingIds.products.length} products and ${missingIds.looks.length} looks missing`,
      missingIds
    );
  }

  // If no partial results wanted and we have missing items, return null
  if ((missingIds.products.length > 0 || missingIds.looks.length > 0) && !includePartial && !throwOnMissing) {
    return null as unknown as CollectionDto;
  }

  // Reconstruct the original collection structure
  // Note: We keep productIds/lookIds as they are required fields in CollectionDto
  const denormalizedCollection: CollectionDto = {
    ...normalizedCollection,
    products: products,
    looks: looks,
  };

  // Store in memoization cache
  memoCache.set(cacheKey, denormalizedCollection);

  return denormalizedCollection;
}

/**
 * Normalizes an array of collections
 * Alias for batchNormalizeCollections for API consistency
 *
 * @param queryClient React Query client instance
 * @param collections Array of collection DTOs
 * @returns Array of normalized collection objects
 */
export function normalizeCollections(queryClient: QueryClient, collections: CollectionDto[]): NormalizedCollection[] {
  return batchNormalizeCollections(queryClient, collections);
}

/**
 * Type guard to check if an object is a normalized collection
 */
export function isNormalizedCollection(obj: any): obj is NormalizedCollection {
  return (
    obj &&
    typeof obj === "object" &&
    "id" in obj &&
    "productIds" in obj &&
    "lookIds" in obj &&
    Array.isArray(obj.productIds) &&
    Array.isArray(obj.lookIds)
  );
}

/**
 * Type guard to check if an object is a denormalized collection
 */
export function isDenormalizedCollection(obj: any): obj is CollectionDto {
  return (
    obj &&
    typeof obj === "object" &&
    "id" in obj &&
    "products" in obj &&
    "looks" in obj &&
    Array.isArray(obj.products) &&
    Array.isArray(obj.looks)
  );
}
